import {
  Button,
  Heading,
  Spinner,
  ToastAlertProvider,
} from '@design-systems/canvas-ui'
import Icon, {
  WarningFillIcon,
  ErrorFillIcon,
} from '@design-systems/react-icons'
import React from 'react'
import { RFI_TYPE_QUESTIONNAIRE } from '../../../../common/commonConstants'
import { useRfi3Context } from '../context/Rfi3Context'
import { useRfi3Actions } from '../context/useRfi3Actions'
import { useRfi3Data } from '../context/useRfi3Data'
import { useRfi3Tabs } from '../context/useRfi3Tabs'
import RFI3ProductCapabilitiesApprovalCards from './ProductCapabilitiesApprovalCards'
import Rfi3Body from './Rfi3Body'
import Rfi3FooterActions from './Rfi3FooterActions'
import Rfi3Header from './Rfi3Header'
import Rfi3Modals from './Rfi3Modals'

const COMMENTS_MAX_LEN_LOCAL = 500

const Rfi3LandingContainer = () => {
  const { state, deps } = useRfi3Context()
  const { env, navigate, userSettings } = deps
  const {
    supplierId,
    locationId,
    manufacturingTypeId,
    commentsTab,
    rfi3Template: ctxTemplate,
    rfi3Data: ctxData,
  } = state

  const {
    rfi3Template,
    rfi3Data,
    rfiMasterData,
    auditData,
    capabilityOverview,
    isReadonly,
    isRfi3DataFetchProgress,
    isRfi3DataFetchFailed,
    errorCache,
    isShowOverlay,
    getAllRFI3Data,
    saveRfi3Payload,
    isPageLoading,
  } = useRfi3Data()

  const {
    activeTab,
    activeRFI3Section,
    setActiveTab,
    setActiveRfi3Section,
    gotoPreviousSection,
    gotoNextSection,
    getTabIdByName,
    isTabErrored,
  } = useRfi3Tabs()

  const actions = useRfi3Actions(getAllRFI3Data)
  const {
    isFastTrackInProgress,
    isFastTrackFailed,
    isFastTrackAvailable,
    isShowBypass,
    isShowEmailDialog,
    isShowConfirmation,
    isRfiActionInProgress,
    isRfiActionFailed,
    refreshRFI3Screen,
    isRfi3ActionAdminEnabled,
    productCapabilityDialogSettings,
    isRfiNextActionPCInProgress,
    isRfiNextActionPCFailed,
    isCommentsOpen,
    isAuditTrailOpen,
    userAction,
    userComments,
    capabilityAudit,
    setUserComments,
    setIsCommentsOpen,
    setIsAuditTrailOpen,
    setIsShowBypass,
    isRfi3ActionButtonEnabled,
    closeEmailDialog,
    closeRFIActionConfirmationModal,
    onActionConfirmation,
    confirmBypassReaction,
    onCapabilityNextActionConfirmation,
    closeRFIProductCapabilitiesConfirmationModal,
    handleFastTrackClick,
    handleRejectClick,
    handleApproveClick,
    onLastRfi3ActivityChange,
    handleCloseBypassModal,
    setProductCapabilityDialogSettings,
    handleViewCapabilityAuditHistory,
    handleOnCapabilityAuditDrawerClose,
  } = actions

  const statusColorMapper = env?.mfelcm?.appConstants?.statusColorMapper

  return (
    <div>
      {isRfi3DataFetchProgress && !isRfi3DataFetchFailed && (
        <div className="height-100 align-centre-centre">
          <Spinner
            key="spinner-rfi3-loading"
            data-testid="spinner-rfi3-loading"
            size="lg"
          />
        </div>
      )}

      {!isRfi3DataFetchProgress && isRfi3DataFetchFailed && (
        <div className="height-100 align-centre-centre">
          <Button
            onClick={() => getAllRFI3Data()}
            variant="ghost"
            isIconOnly
            size="lg"
            aria-label="RFI3 Landing fetch failed - click to retry"
          >
            <Icon svg={ErrorFillIcon} className="hc8-clr-warning icon-large" />
          </Button>
        </div>
      )}

      {!isRfi3DataFetchProgress &&
        !isRfi3DataFetchFailed &&
        rfi3Template &&
        rfi3Data && (
          <>
            <ToastAlertProvider>
              {/* Keep review modal in Modals component; here we just render header + body */}
            </ToastAlertProvider>
            <Rfi3Header
              rfi3Data={rfi3Data}
              rfi3Template={rfi3Template}
              navigate={navigate}
              isAuditTrailOpen={isAuditTrailOpen}
              isCommentsOpen={isCommentsOpen}
              setIsAuditTrailOpen={setIsAuditTrailOpen}
              setIsCommentsOpen={setIsCommentsOpen}
            />
            <RFI3ProductCapabilitiesApprovalCards
              rfi3Template={rfi3Template}
              setProductCapabilityDialogSettings={
                setProductCapabilityDialogSettings
              }
              productCapabilityDialogSettings={productCapabilityDialogSettings}
              capabilityList={capabilityOverview}
              onViewHistory={handleViewCapabilityAuditHistory}
              onCapabilityAuditDrawerClose={handleOnCapabilityAuditDrawerClose}
              isReadonly={isReadonly}
              manufacturingTypeId={manufacturingTypeId}
              auditData={auditData}
              isCapabilityAuditOpen={capabilityAudit?.isVisible}
              rfiType={RFI_TYPE_QUESTIONNAIRE}
              supplierId={supplierId}
              locationId={locationId}
            />
            <Rfi3Body
              rfi3Template={rfi3Template}
              rfi3Data={rfi3Data}
              rfiMasterData={rfiMasterData}
              isReadonly={isReadonly}
              auditData={auditData}
              activeTab={activeTab}
              activeRFI3Section={activeRFI3Section}
              setActiveRfi3Section={setActiveRfi3Section}
              errorCache={errorCache}
              gotoPreviousSection={gotoPreviousSection}
              gotoNextSection={gotoNextSection}
              saveRfi3Payload={saveRfi3Payload}
              statusColorMapper={statusColorMapper}
              productCapabilityDialogSettings={productCapabilityDialogSettings}
              setProductCapabilityDialogSettings={
                setProductCapabilityDialogSettings
              }
              isRfi3SaveInProgress={state.isRfi3SaveInProgress}
              isRfi3SaveFailed={state.isRfi3SaveFailed}
              isTabErrored={isTabErrored}
              getTabIdByName={getTabIdByName}
              setActiveTab={setActiveTab}
            />
          </>
        )}

      {!isRfi3DataFetchProgress &&
        !isRfi3DataFetchFailed &&
        (!rfi3Template || !rfi3Data) && (
          <div className="height-100 align-centre-centre">
            <Icon svg={WarningFillIcon} aria-hidden />
            <Heading className="hc8-ml-2x" size={3}>
              RFI3 not found
            </Heading>
          </div>
        )}

      <Rfi3FooterActions
        isRfi3SaveInProgress={state.isRfi3SaveInProgress}
        isRfi3SaveFailed={state.isRfi3SaveFailed}
        isRfi3DataFetchFailed={isRfi3DataFetchFailed}
        isRfi3DataFetchProgress={isRfi3DataFetchProgress}
        rfi3Template={rfi3Template}
        rfi3Data={rfi3Data}
        isReadonly={isReadonly}
        isRfi3ActionAdminEnabled={isRfi3ActionAdminEnabled}
        isFastTrackAvailable={isFastTrackAvailable}
        isFastTrackInProgress={isFastTrackInProgress}
        isFastTrackFailed={isFastTrackFailed}
        isRfiActionInProgress={isRfiActionInProgress}
        isRfiActionFailed={isRfiActionFailed}
        userAction={userAction}
        navigate={navigate}
        userSettings={userSettings}
        onFastTrackClick={handleFastTrackClick}
        onRejectClick={handleRejectClick}
        onApproveClick={handleApproveClick}
        isRfi3ActionButtonEnabled={isRfi3ActionButtonEnabled}
        isPageLoading={isPageLoading}
        env={env}
      />

      {rfi3Data && (
        <Rfi3Modals
          isShowEmailDialog={isShowEmailDialog}
          closeEmailDialog={closeEmailDialog}
          isShowConfirmation={isShowConfirmation}
          userAction={userAction}
          userComments={userComments}
          setUserComments={setUserComments}
          commentsMaxLen={COMMENTS_MAX_LEN_LOCAL}
          closeRFIActionConfirmationModal={closeRFIActionConfirmationModal}
          onActionConfirmation={onActionConfirmation}
          rfi3Data={rfi3Data}
          supplierId={supplierId}
          locationId={locationId}
          manufacturingTypeId={manufacturingTypeId}
          isCommentsOpen={isCommentsOpen}
          setIsCommentsOpen={setIsCommentsOpen}
          isAuditTrailOpen={isAuditTrailOpen}
          setIsAuditTrailOpen={setIsAuditTrailOpen}
          refreshRFI3Screen={refreshRFI3Screen}
          onLastRfi3ActivityChange={onLastRfi3ActivityChange}
          isShowBypass={isShowBypass}
          setIsShowBypass={setIsShowBypass}
          handleCloseBypassModal={handleCloseBypassModal}
          confirmBypassReaction={confirmBypassReaction}
          isFastTrackInProgress={isFastTrackInProgress}
          isFastTrackFailed={isFastTrackFailed}
          isRfiNextActionPCInProgress={isRfiNextActionPCInProgress}
          isRfiNextActionPCFailed={isRfiNextActionPCFailed}
          productCapabilityDialogSettings={productCapabilityDialogSettings}
          closeRFIProductCapabilitiesConfirmationModal={
            closeRFIProductCapabilitiesConfirmationModal
          }
          onCapabilityNextActionConfirmation={
            onCapabilityNextActionConfirmation
          }
          isShowOverlay={isShowOverlay}
          commentsTab={commentsTab}
        />
      )}
    </div>
  )
}

export default React.memo(Rfi3LandingContainer)
